import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ShieldAlert, Sparkles, Send, LogOut, FileText, Upload,
  Check, X, ImagePlus, ScrollText, Building2, Loader2, CircleAlert, Gavel,
} from "lucide-react";
import { PARTNER_TERMS, PARTNER_TERMS_VERSION } from "@/lib/partnerTerms";

const AMENITY_OPTIONS = [
  "Free WiFi", "Pool", "Spa", "Restaurant", "Free Parking", "AC",
  "Gym", "Bar", "Banquet Hall", "EV Charging", "Rooftop", "Sea View",
  "Airport Shuttle", "Pet Friendly",
];
const CATEGORIES = ["3-star", "4-star", "5-star", "boutique", "resort", "heritage", "business", "homestay"];

const EMPTY = {
  name: "", city: "", address: "", description: "", category: "3-star", base_price: 3000,
  contact_name: "", contact_email: "", contact_phone: "",
  amenities: [], photos: [],
  gst: "", gst_url: "", pan: "", pan_url: "",
  bank_details: "", bank_proof_url: "", trade_license_url: "", owner_id_proof_url: "",
  checkin_time: "13:00", checkout_time: "11:00",
};

/**
 * PartnerOnboarding — the ONLY page visible to hotel_partner_pending users.
 *
 * Complete-your-profile flow with 4 tabs:
 *   1. Basic info + contact
 *   2. Photos (min 3) — real file upload to Emergent object storage
 *   3. Amenities & house rules
 *   4. Documents — GST, PAN, Bank proof, Trade License, Owner ID (real uploads)
 *
 * The "Submit for admin review" button is disabled until ALL required fields
 * + all 4 documents + 3+ photos are complete. The server re-validates on submit.
 */
export default function PartnerOnboarding() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    ...EMPTY,
    contact_name: user?.name || "",
    contact_phone: user?.phone || "",
  });
  const [hotelDoc, setHotelDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsScrolled, setTermsScrolled] = useState(false);
  const termsScrollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/my/hotel");
      if (data?.hotel) {
        setHotelDoc(data.hotel);
        setForm((f) => ({ ...f, ...data.hotel, amenities: data.hotel.amenities || [], photos: data.hotel.photos || [] }));
      }
    } catch (e) { console.debug("load failed:", e?.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const saveDraft = async (silent = false) => {
    if (!form.name || !form.city || !form.address || !form.contact_phone) {
      if (!silent) toast.error("Fill hotel name, city, address and contact phone first.");
      return false;
    }
    setSaving(true);
    try {
      await api.put("/my/hotel", { ...form, base_price: Number(form.base_price) });
      if (!silent) toast.success("Draft saved.");
      const { data } = await api.get("/my/hotel");
      if (data?.hotel) setHotelDoc(data.hotel);
      return true;
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Save failed");
      return false;
    } finally { setSaving(false); }
  };

  const submitForReview = async () => {
    if (!termsAccepted) { toast.error("Please read and accept the Partner Terms & Conditions."); return; }
    // Save first, then submit
    const ok = await saveDraft(true);
    if (!ok) { toast.error("Please complete required fields before submitting."); return; }
    setSubmitting(true);
    try {
      await api.post("/my/hotel/submit-for-review", {
        terms_accepted: true, terms_version: PARTNER_TERMS_VERSION,
      });
      toast.success("Submitted! We'll review within 24-48 hrs.");
      if (refresh) await refresh();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  // ---------- File upload helper ----------
  const uploadFile = async (kind, file) => {
    if (!file) return null;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max file size is 5 MB"); return null; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/uploads/hotel-doc?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data.download_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
      return null;
    }
  };

  const uploadDoc = async (kind, field, file) => {
    const url = await uploadFile(kind, file);
    if (!url) return;
    // Save draft immediately so refresh preserves it and the completeness check sees it
    const nextForm = { ...form, [field]: url };
    setForm(nextForm);
    setSaving(true);
    try {
      await api.put("/my/hotel", { ...nextForm, base_price: Number(nextForm.base_price) });
      toast.success(`${kind.toUpperCase()} uploaded ✓`);
      const { data } = await api.get("/my/hotel");
      if (data?.hotel) setHotelDoc(data.hotel);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed after upload");
    } finally { setSaving(false); }
  };

  const uploadPhoto = async (file) => {
    const url = await uploadFile("photo", file);
    if (!url) return;
    const nextForm = { ...form, photos: [...(form.photos || []), url] };
    setForm(nextForm);
    setSaving(true);
    try {
      await api.put("/my/hotel", { ...nextForm, base_price: Number(nextForm.base_price) });
      toast.success("Photo added ✓");
      const { data } = await api.get("/my/hotel");
      if (data?.hotel) setHotelDoc(data.hotel);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const removePhoto = async (idx) => {
    const nextForm = { ...form, photos: form.photos.filter((_, i) => i !== idx) };
    setForm(nextForm);
    try { await api.put("/my/hotel", { ...nextForm, base_price: Number(nextForm.base_price) }); }
    catch (e) { toast.error("Could not remove"); }
  };

  const toggleAmenity = (a) => setForm((f) => ({
    ...f,
    amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a],
  }));

  // ---------- Completeness check (mirrors server-side gate) ----------
  const missing = useMemo(() => {
    const m = [];
    if (!form.name?.trim()) m.push("Hotel name");
    if (!form.city?.trim()) m.push("City");
    if (!form.address?.trim()) m.push("Address");
    if (!form.contact_phone?.trim()) m.push("Contact phone");
    if (!form.category) m.push("Category");
    if (!form.gst?.trim()) m.push("GST number");
    if (!form.gst_url) m.push("GST certificate upload");
    if (!form.pan?.trim()) m.push("PAN number");
    if (!form.pan_url) m.push("PAN document upload");
    if (!form.bank_details?.trim()) m.push("Bank details");
    if (!form.bank_proof_url) m.push("Bank proof upload");
    if (!form.trade_license_url) m.push("Trade license upload");
    if ((form.photos || []).length < 3) m.push(`At least 3 photos (${(form.photos || []).length}/3)`);
    return m;
  }, [form]);

  const submitted = !!hotelDoc?.submitted_for_review;
  const approved = hotelDoc?.status === "approved";

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-900 to-emerald-900">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 15% 30%, rgba(16,185,129,0.4) 0px, transparent 50%), radial-gradient(circle at 85% 70%, rgba(99,102,241,0.5) 0px, transparent 50%)" }} />
        <div className="relative max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-10">
          <div className="hw-label text-amber-300 flex items-center gap-2">
            <ShieldAlert className="w-3 h-3" />
            {approved ? "Approved" : submitted ? "Under review" : "Complete your hotel profile"}
          </div>
          <h1 className="hw-head text-3xl md:text-5xl font-black text-white mt-3" data-testid="onboarding-heading">
            Welcome, {user?.name?.split(" ")[0]}!
          </h1>
          <p className="text-slate-200 mt-3 max-w-2xl leading-relaxed">
            Submit your hotel details, photos, and verification documents below. Our team reviews every new partner within 24-48 hours. Once approved, you unlock rooms, leads, AI Auto-Bidding, and payouts.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 pb-16 space-y-5">
        {submitted && !approved && (
          <div className="hw-card p-5 border-l-4 border-l-amber-500 bg-amber-50" data-testid="submitted-status-card">
            <div className="flex items-center gap-2 text-amber-800">
              <Sparkles className="w-4 h-4" />
              <div className="hw-head font-black">Submitted — under review</div>
            </div>
            <p className="text-sm text-slate-700 mt-2">You&apos;ll get a notification once approved. Meanwhile, you can update your submission and add more photos below.</p>
          </div>
        )}

        {/* Iter47 — Chain mode toggle (only shown to fresh signups so it doesn't
            confuse partners already in flight). Stores on user.is_chain via
            /api/my/chain. When ON, after approval the partner can add more
            properties via the switcher. */}
        {!submitted && <ChainModeCard />}

        {/* Completeness banner */}
        {!submitted && (
          <div
            className={`hw-card p-4 border-l-4 ${missing.length === 0 ? "border-l-emerald-500 bg-emerald-50" : "border-l-blue-500 bg-blue-50"}`}
            data-testid="completeness-banner"
          >
            <div className="flex items-center gap-2">
              {missing.length === 0
                ? <><Check className="w-4 h-4 text-emerald-700" /><span className="hw-head font-black text-emerald-800">All set — ready to submit for admin review</span></>
                : <><CircleAlert className="w-4 h-4 text-blue-700" /><span className="hw-head font-black text-blue-800">{missing.length} item{missing.length > 1 ? "s" : ""} pending before you can submit</span></>
              }
            </div>
            {missing.length > 0 && (
              <ul className="text-sm text-slate-700 mt-2 grid md:grid-cols-2 gap-x-4 gap-y-1 list-disc list-inside" data-testid="missing-list">
                {missing.map((m) => <li key={m}>{m}</li>)}
              </ul>
            )}
          </div>
        )}

        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="bg-white rounded-sm border border-slate-200 p-1 flex flex-wrap gap-1">
            <TabsTrigger value="basic" data-testid="tab-basic" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-sm font-bold"><Building2 className="w-4 h-4 mr-2" />Basic Info</TabsTrigger>
            <TabsTrigger value="photos" data-testid="tab-photos" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-sm font-bold"><ImagePlus className="w-4 h-4 mr-2" />Photos ({(form.photos || []).length})</TabsTrigger>
            <TabsTrigger value="amenities" data-testid="tab-amenities" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-sm font-bold"><ScrollText className="w-4 h-4 mr-2" />Amenities & Rules</TabsTrigger>
            <TabsTrigger value="docs" data-testid="tab-docs" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-sm font-bold"><FileText className="w-4 h-4 mr-2" />Documents</TabsTrigger>
          </TabsList>

          {/* ---------- BASIC INFO ---------- */}
          <TabsContent value="basic" className="mt-4">
            <div className="hw-card p-6 md:p-8 space-y-4" data-testid="onboarding-form">
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Hotel name *" v={form.name} on={(v) => setForm({ ...form, name: v })} tid="ob-name" />
                <Field label="City *" v={form.city} on={(v) => setForm({ ...form, city: v })} tid="ob-city" />
              </div>
              <Field label="Full address *" v={form.address} on={(v) => setForm({ ...form, address: v })} tid="ob-address" />
              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <Label className="hw-label">Category *</Label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="rounded-sm mt-1 w-full h-10 border border-slate-200 px-3 text-sm" data-testid="ob-category">
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <Field label="Starting price (₹)" type="number" v={form.base_price} on={(v) => setForm({ ...form, base_price: v })} tid="ob-price" />
                <Field label="Contact phone *" v={form.contact_phone} on={(v) => setForm({ ...form, contact_phone: v })} tid="ob-phone" />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Contact person" v={form.contact_name} on={(v) => setForm({ ...form, contact_name: v })} tid="ob-contact-name" />
                <Field label="Contact email" v={form.contact_email} on={(v) => setForm({ ...form, contact_email: v })} tid="ob-contact-email" />
              </div>
              <div>
                <Label className="hw-label">Describe your hotel</Label>
                <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm mt-1" placeholder="What makes your hotel special?" data-testid="ob-desc" />
              </div>
              <Button onClick={() => saveDraft(false)} disabled={saving} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold py-5 h-auto" data-testid="ob-save-basic">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Save basic info
              </Button>
            </div>
          </TabsContent>

          {/* ---------- PHOTOS ---------- */}
          <TabsContent value="photos" className="mt-4">
            <div className="hw-card p-6 md:p-8 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="hw-head text-lg font-black">Hotel photos</div>
                  <div className="text-sm text-slate-600">Upload at least 3 clear photos (front, lobby, rooms). JPG / PNG / WebP, max 5 MB each.</div>
                </div>
                <FileButton kind="photo" onFile={uploadPhoto} accept="image/jpeg,image/png,image/webp" tid="ob-photo-add" label="Upload photo" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {(form.photos || []).map((p, i) => (
                  <div key={p + i} className="relative aspect-square bg-slate-100 rounded-sm overflow-hidden">
                    <ProtectedImage src={p} alt={`Photo ${i + 1}`} />
                    <button type="button" onClick={() => removePhoto(i)}
                      className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white w-6 h-6 grid place-items-center rounded-full"
                      data-testid={`ob-photo-remove-${i}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {(form.photos || []).length === 0 && (
                  <div className="col-span-full text-center py-8 text-slate-400 text-sm">No photos yet.</div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ---------- AMENITIES ---------- */}
          <TabsContent value="amenities" className="mt-4">
            <div className="hw-card p-6 md:p-8 space-y-5">
              <div>
                <div className="hw-head text-lg font-black">Amenities</div>
                <div className="text-sm text-slate-600 mb-3">Pick everything your hotel offers.</div>
                <div className="flex flex-wrap gap-2">
                  {AMENITY_OPTIONS.map((a) => {
                    const active = form.amenities.includes(a);
                    return (
                      <button key={a} type="button" onClick={() => toggleAmenity(a)} data-testid={`ob-amenity-${a}`}
                        className={`hw-chip border ${active ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"}`}>
                        {active && <Check className="w-3 h-3 mr-1 inline" />}{a}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label className="hw-label">Check-in time</Label><Input type="time" value={form.checkin_time} onChange={(e) => setForm({ ...form, checkin_time: e.target.value })} className="rounded-sm mt-1" data-testid="ob-checkin" /></div>
                <div><Label className="hw-label">Check-out time</Label><Input type="time" value={form.checkout_time} onChange={(e) => setForm({ ...form, checkout_time: e.target.value })} className="rounded-sm mt-1" data-testid="ob-checkout" /></div>
              </div>
              <Button onClick={() => saveDraft(false)} disabled={saving} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold py-5 h-auto" data-testid="ob-save-amenities">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Save amenities & rules
              </Button>
            </div>
          </TabsContent>

          {/* ---------- DOCUMENTS ---------- */}
          <TabsContent value="docs" className="mt-4">
            <div className="hw-card p-6 md:p-8 space-y-5" data-testid="documents-panel">
              <div>
                <div className="hw-head text-lg font-black">Verification documents</div>
                <div className="text-sm text-slate-600">All four documents are required for admin approval. Files stored securely — only you and our admins can view them.</div>
              </div>

              <DocRow
                title="GST"
                numberLabel="GST number"
                numberValue={form.gst}
                onNumberChange={(v) => setForm({ ...form, gst: v.toUpperCase().replace(/\s|-/g, "") })}
                numberTid="ob-gst"
                fileLabel="GST certificate"
                fileUrl={form.gst_url}
                onUpload={(f) => uploadDoc("gst", "gst_url", f)}
                uploadTid="ob-gst-upload"
                accept="application/pdf,image/jpeg,image/png"
                placeholder="29ABCDE1234F1Z5"
                validate={(v) => /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(v)}
                validateHint="Must be 15 chars: 2 state digits + 10-char PAN + entity + Z + checksum"
              />
              <DocRow
                title="PAN"
                numberLabel="PAN number"
                numberValue={form.pan}
                onNumberChange={(v) => setForm({ ...form, pan: v.toUpperCase().replace(/\s|-/g, "") })}
                numberTid="ob-pan"
                fileLabel="PAN card"
                fileUrl={form.pan_url}
                onUpload={(f) => uploadDoc("pan", "pan_url", f)}
                uploadTid="ob-pan-upload"
                accept="application/pdf,image/jpeg,image/png"
                placeholder="ABCDE1234F"
                validate={(v) => /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(v)}
                validateHint="Must be 10 chars: 5 letters + 4 digits + 1 letter"
              />
              <DocRow
                title="Bank"
                numberLabel="Bank details (Bank • Account • IFSC)"
                numberValue={form.bank_details}
                onNumberChange={(v) => setForm({ ...form, bank_details: v })}
                numberTid="ob-bank"
                fileLabel="Cancelled cheque / bank proof"
                fileUrl={form.bank_proof_url}
                onUpload={(f) => uploadDoc("bank_proof", "bank_proof_url", f)}
                uploadTid="ob-bank-upload"
                accept="application/pdf,image/jpeg,image/png"
                placeholder="HDFC • XXXX1234 • HDFC0001234"
              />
              <DocRow
                title="Trade License"
                fileLabel="Trade license document"
                fileUrl={form.trade_license_url}
                onUpload={(f) => uploadDoc("trade_license", "trade_license_url", f)}
                uploadTid="ob-trade-upload"
                accept="application/pdf,image/jpeg,image/png"
              />
              <DocRow
                title="Owner ID (optional)"
                fileLabel="Aadhar / Passport / Driving License"
                fileUrl={form.owner_id_proof_url}
                onUpload={(f) => uploadDoc("owner_id", "owner_id_proof_url", f)}
                uploadTid="ob-ownerid-upload"
                accept="application/pdf,image/jpeg,image/png"
              />
            </div>
          </TabsContent>
        </Tabs>

        {/* ---------- T&C acceptance (required before submit) ---------- */}
        {!approved && (
          <div className="hw-card p-5 md:p-6 border-l-4 border-l-slate-900" data-testid="terms-card">
            <div className="flex items-center gap-2">
              <Gavel className="w-5 h-5 text-slate-900" />
              <div className="hw-head text-lg font-black text-slate-900">Partner Terms & Conditions</div>
              <span className="hw-chip border-slate-300 bg-slate-50 text-slate-600 text-xs">v{PARTNER_TERMS_VERSION}</span>
            </div>
            <p className="text-sm text-slate-600 mt-2">
              Please read every clause carefully — you must accept these terms before your listing can go to admin review. Scroll to the bottom to enable the acceptance checkbox.
            </p>
            <div
              ref={termsScrollRef}
              onScroll={(e) => {
                const el = e.currentTarget;
                if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) setTermsScrolled(true);
              }}
              data-testid="terms-scroll"
              className="mt-4 h-72 overflow-y-auto border border-slate-200 rounded-sm p-4 bg-slate-50 space-y-4 text-sm text-slate-800"
            >
              {PARTNER_TERMS.map((t) => (
                <div key={t.title}>
                  <div className="hw-head font-black text-slate-900">{t.title}</div>
                  <p className="mt-1 leading-relaxed text-slate-700">{t.body}</p>
                </div>
              ))}
              <div className="text-xs text-slate-500 pt-2 border-t border-slate-200">
                End of terms — you may now check the acceptance box below.
              </div>
            </div>
            <label
              className={`mt-4 flex items-start gap-3 p-3 border rounded-sm cursor-pointer select-none transition ${
                termsScrolled ? "border-slate-300 bg-white hover:bg-slate-50" : "border-slate-200 bg-slate-100 opacity-60 cursor-not-allowed"
              }`}
              data-testid="terms-accept-row"
            >
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 accent-emerald-600"
                checked={termsAccepted}
                disabled={!termsScrolled}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                data-testid="terms-accept-checkbox"
              />
              <span className="text-sm text-slate-800">
                <span className="font-bold">I have read and accept the SwaanStay Partner Terms & Conditions ({PARTNER_TERMS_VERSION}).</span>{" "}
                I confirm I am the legal owner or authorised signatory of the hotel, all information submitted is true, all uploaded documents are genuine, and I agree to be bound by every clause above. I understand that misrepresentation may result in permanent delisting and legal action.
              </span>
            </label>
            {!termsScrolled && (
              <div className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                <CircleAlert className="w-3 h-3" /> Scroll through all clauses above to unlock the acceptance checkbox.
              </div>
            )}
          </div>
        )}

        {/* ---------- Sticky submit ---------- */}
        {!approved && (
          <div className="hw-card p-5 md:p-6 border-2 border-emerald-500 bg-emerald-50" data-testid="submit-card">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1">
                <div className="hw-head text-lg font-black text-slate-900">
                  {submitted ? "Update your submission" : "Ready for admin review?"}
                </div>
                <div className="text-sm text-slate-700 mt-1">
                  {missing.length === 0 && termsAccepted
                    ? "All required items are complete and terms accepted. Click submit to send your profile to our admin team."
                    : missing.length > 0
                      ? `Complete the ${missing.length} pending item${missing.length > 1 ? "s" : ""} shown above, then accept the terms and submit.`
                      : "Accept the Partner Terms & Conditions above to enable submission."}
                </div>
              </div>
              <Button
                onClick={submitForReview}
                disabled={submitting || missing.length > 0 || !termsAccepted}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold px-6 py-6 h-auto disabled:opacity-50"
                data-testid="ob-submit-review-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {submitted ? "Re-submit for review" : "Submit for admin review"}
              </Button>
            </div>
          </div>
        )}

        <div className="text-center text-xs text-slate-500">
          {approved ? "Approved — refresh to unlock your dashboard." : "The partner dashboard, rooms, leads and AI features unlock after admin review."}
          <Link to="/logout" className="ml-3 text-blue-700 hover:underline"><LogOut className="w-3 h-3 inline" /> Sign out</Link>
        </div>
      </div>
    </div>
  );
}

// ---------- Small helpers ----------

function Field({ label, v, on, tid, type = "text" }) {
  return (
    <div>
      <Label className="hw-label">{label}</Label>
      <Input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="rounded-sm mt-1" data-testid={tid} />
    </div>
  );
}

function FileButton({ kind, onFile, accept, tid, label }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!f) return;
    setBusy(true);
    try { await onFile(f); } finally { setBusy(false); }
  };
  return (
    <>
      <input ref={inputRef} type="file" hidden accept={accept} onChange={handle} data-testid={`${tid}-input`} />
      <Button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid={tid}>
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
        {busy ? "Uploading…" : (label || `Upload ${kind}`)}
      </Button>
    </>
  );
}

function DocRow({ title, numberLabel, numberValue, onNumberChange, numberTid, fileLabel, fileUrl, onUpload, uploadTid, accept, placeholder, validate, validateHint }) {
  const showValid = validate && numberValue;
  const isValid = showValid ? validate(numberValue) : null;
  return (
    <div className="hw-card border border-slate-200 p-4 bg-white">
      <div className="hw-label text-slate-500 mb-2">{title}</div>
      <div className="grid md:grid-cols-2 gap-3">
        {numberLabel && (
          <div>
            <Label className="hw-label">{numberLabel}</Label>
            <Input
              value={numberValue || ""}
              onChange={(e) => onNumberChange(e.target.value)}
              className={`rounded-sm mt-1 ${showValid ? (isValid ? "border-emerald-500 focus:border-emerald-500" : "border-red-500 focus:border-red-500") : ""}`}
              placeholder={placeholder}
              data-testid={numberTid}
            />
            {showValid && !isValid && (
              <div className="text-xs text-red-700 mt-1" data-testid={`${numberTid}-error`}>
                ✗ {validateHint}
              </div>
            )}
            {showValid && isValid && (
              <div className="text-xs text-emerald-700 mt-1" data-testid={`${numberTid}-ok`}>
                ✓ Valid format
              </div>
            )}
          </div>
        )}
        <div className={numberLabel ? "" : "md:col-span-2"}>
          <Label className="hw-label">{fileLabel}</Label>
          <div className="mt-1 flex items-center gap-2">
            {fileUrl ? (
              <>
                <a href={fileUrl.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${fileUrl}` : fileUrl}
                  target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline text-sm font-semibold flex items-center gap-1"
                  data-testid={`${uploadTid}-view`}>
                  <Check className="w-4 h-4" /> Uploaded — view
                </a>
                <FileButton kind={title} onFile={onUpload} accept={accept} tid={`${uploadTid}-replace`} label="Replace" />
              </>
            ) : (
              <FileButton kind={title} onFile={onUpload} accept={accept} tid={uploadTid} label="Upload file" />
            )}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">PDF or JPG/PNG · max 5 MB · must be non-empty</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Backend requires auth to serve uploaded files, and <img src=""> can't send
 * headers. We fetch as blob using the axios instance's Authorization interceptor,
 * then render a blob URL. Blob URLs are revoked on unmount to avoid leaks.
 */
function ProtectedImage({ src, alt }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let objectUrl;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(src.replace(/^\/api/, ""), { responseType: "blob" });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setBlobUrl(objectUrl);
      } catch (e) { console.debug("image load failed:", e?.message); }
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [src]);
  if (!blobUrl) return <div className="w-full h-full grid place-items-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  return <img src={blobUrl} alt={alt} className="w-full h-full object-cover" />;
}


// Iter47 — inline chain-mode enable card.
function ChainModeCard() {
  const [chain, setChain] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/my/chain").then(({ data }) => {
      setChain(data);
      if (data?.company_name) setCompanyName(data.company_name);
    }).catch(() => setChain({ is_chain: false }));
  }, []);
  if (!chain) return null;

  const enable = async () => {
    setSaving(true);
    try {
      await api.patch("/my/chain", { is_chain: true, chain_company_name: companyName || undefined });
      const { data } = await api.get("/my/chain");
      setChain(data);
      toast.success("Chain mode enabled. You'll be able to add more properties after approval.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not enable chain mode");
    } finally { setSaving(false); }
  };
  const disable = async () => {
    setSaving(true);
    try {
      await api.patch("/my/chain", { is_chain: false });
      const { data } = await api.get("/my/chain");
      setChain(data);
      toast.success("Chain mode disabled");
    } catch { toast.error("Could not disable"); }
    finally { setSaving(false); }
  };

  return (
    <div className="hw-card p-5 border-l-4 border-l-indigo-500 bg-indigo-50/40" data-testid="chain-mode-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="hw-label text-indigo-700">Property type</div>
          <div className="hw-head text-lg font-black text-slate-900 mt-0.5">
            {chain.is_chain ? "You're onboarding as a Chain (multi-property)." : "Are you a single hotel or a chain of properties?"}
          </div>
          <p className="text-sm text-slate-600 mt-1">
            {chain.is_chain
              ? <>Great — after this first property is approved, add more via the property switcher in the header.</>
              : <>Single hotels can leave this off. Chains (2+ properties under one company) should turn it on now to unlock the multi-property dashboard.</>}
          </p>
          {chain.is_chain && (
            <div className="mt-3 max-w-md">
              <Label className="hw-label">Company name</Label>
              <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                     onBlur={() => companyName && api.patch("/my/chain", { chain_company_name: companyName }).catch(() => {})}
                     placeholder="e.g. Swaan Hospitality Pvt Ltd"
                     className="rounded-sm mt-1 max-w-md" data-testid="chain-company-name" />
            </div>
          )}
        </div>
        <div className="shrink-0">
          {chain.is_chain ? (
            <Button variant="outline" onClick={disable} disabled={saving} className="rounded-sm" data-testid="chain-disable">
              Switch to single hotel
            </Button>
          ) : (
            <Button onClick={enable} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-sm font-bold" data-testid="chain-enable">
              I run a chain
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
